import React from 'react';
import { FaGithub, FaLinkedin, FaEnvelope, FaPhone, FaMapMarkerAlt, FaHeart, FaTwitter, FaCode } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --accent:  #00f5d4;
          --accent2: #7b61ff;
          --accent3: #ff4ecd;
          --bg:      #07090f;
          --text:    #e8eaf6;
          --muted:   rgba(232,234,246,0.45);
          --border:  rgba(0,245,212,0.13);
          --card-bg: rgba(255,255,255,0.03);
        }

        .ft-section {
          position: relative;
          background: var(--bg);
          font-family: 'Syne', sans-serif;
          overflow: hidden;
          border-top: 1px solid var(--border);
        }

        .ft-section::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          background:
            radial-gradient(ellipse 50% 60% at 80% 20%, rgba(123,97,255,0.05) 0%, transparent 70%),
            radial-gradient(ellipse 40% 50% at 10% 80%, rgba(0,245,212,0.03) 0%, transparent 70%);
          pointer-events: none;
        }

        .ft-inner {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          padding: 3rem 2rem 2rem;
        }

        .ft-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          margin-bottom: 2rem;
        }

        /* Footer Headings */
        .ft-title {
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
          background: linear-gradient(135deg, #fff, var(--accent));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          letter-spacing: -0.02em;
        }

        .ft-subtitle {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 1rem;
        }

        .ft-text {
          font-size: 0.85rem;
          color: var(--muted);
          line-height: 1.7;
        }

        /* Social Links */
        .ft-social {
          display: flex;
          gap: 1rem;
          margin-top: 1.25rem;
        }

        .ft-social-link {
          width: 38px;
          height: 38px;
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text);
          font-size: 1.1rem;
          transition: all 0.3s ease;
        }

        .ft-social-link:hover {
          border-color: var(--accent);
          background: rgba(0,245,212,0.1);
          color: var(--accent);
          transform: translateY(-3px);
        }

        /* Quick Links */
        .ft-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .ft-links li {
          margin-bottom: 0.7rem;
        }

        .ft-links a {
          color: var(--muted);
          text-decoration: none;
          font-size: 0.85rem;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .ft-links a::before {
          content: '▹';
          color: var(--accent2);
          font-size: 0.7rem;
          opacity: 0;
          transition: opacity 0.2s, transform 0.2s;
        }

        .ft-links a:hover {
          color: var(--accent);
          transform: translateX(5px);
        }

        .ft-links a:hover::before {
          opacity: 1;
        }

        /* Contact Info */
        .ft-contact-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .ft-contact-list li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.8rem;
          font-size: 0.85rem;
          color: var(--muted);
        }

        .ft-contact-list svg {
          color: var(--accent);
          font-size: 0.9rem;
          min-width: 18px;
        }

        .ft-contact-list a {
          color: var(--muted);
          text-decoration: none;
          transition: color 0.2s;
        }

        .ft-contact-list a:hover {
          color: var(--accent);
        }

        /* Badges */
        .ft-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .ft-badge {
          background: rgba(0,245,212,0.1);
          border: 1px solid rgba(0,245,212,0.2);
          border-radius: 40px;
          padding: 0.25rem 0.8rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.6rem;
          font-weight: 500;
          color: var(--accent);
          letter-spacing: 0.02em;
        }

        .ft-badge-info {
          background: rgba(123,97,255,0.1);
          border-color: rgba(123,97,255,0.2);
          color: var(--accent2);
        }

        /* Divider */
        .ft-divider {
          border: none;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--accent), var(--accent2), var(--accent3), transparent);
          margin: 2rem 0 1.5rem;
        }

        /* Copyright */
        .ft-copyright {
          text-align: center;
        }

        .ft-copyright p {
          font-size: 0.8rem;
          color: var(--muted);
          margin-bottom: 0.5rem;
        }

        .ft-copyright small {
          font-size: 0.7rem;
          color: rgba(232,234,246,0.3);
        }

        .ft-heart {
          color: var(--accent3);
          animation: ftHeartbeat 1.5s ease infinite;
          display: inline-block;
        }

        @keyframes ftHeartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        /* Scroll to top button */
        .ft-scroll-top {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          width: 45px;
          height: 45px;
          background: var(--card-bg);
          border: 1px solid var(--accent);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent);
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 100;
          opacity: 0;
          visibility: hidden;
        }

        .ft-scroll-top.visible {
          opacity: 1;
          visibility: visible;
        }

        .ft-scroll-top:hover {
          background: var(--accent);
          color: var(--bg);
          transform: translateY(-5px);
          box-shadow: 0 5px 20px rgba(0,245,212,0.3);
        }

        @media (max-width: 900px) {
          .ft-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }
        }

        @media (max-width: 600px) {
          .ft-inner { padding: 2rem 1.5rem 1.5rem; }
          .ft-grid { grid-template-columns: 1fr; gap: 2rem; }
        }
      `}</style>

      <footer className="ft-section">
        <div className="ft-inner">
          <div className="ft-grid">
            {/* About Section */}
            <div>
              <h3 className="ft-title">Subodh Paswan</h3>
              <div className="ft-subtitle">Full Stack Developer</div>
              <p className="ft-text">
                Specializing in MERN Stack development. Passionate about building 
                scalable web applications and continuous learning. Open to 
                exciting opportunities and collaborations.
              </p>
              <div className="ft-social">
                <a 
                  href="https://github.com/SuboKumar2356" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="ft-social-link"
                  aria-label="GitHub"
                >
                  <FaGithub />
                </a>
                <a 
                  href="https://linkedin.com/in/subokumar2356" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="ft-social-link"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin />
                </a>
                <a 
                  href="https://twitter.com/subokumar2356" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="ft-social-link"
                  aria-label="Twitter"
                >
                  <FaTwitter />
                </a>
                <a 
                  href="mailto:subo2356@gmail.com" 
                  className="ft-social-link"
                  aria-label="Email"
                >
                  <FaEnvelope />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="ft-subtitle">Navigation</h4>
              <ul className="ft-links">
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#skills">Skills</a></li>
                <li><a href="#projects">Projects</a></li>
                <li><a href="#experience">Experience</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="ft-subtitle">Get in Touch</h4>
              <ul className="ft-contact-list">
                <li>
                  <FaEnvelope />
                  <a href="mailto:subo2356@gmail.com">subo2356@gmail.com</a>
                </li>
                <li>
                  <FaPhone />
                  <a href="tel:+919851241219">+91 9851241219</a>
                </li>
                <li>
                  <FaMapMarkerAlt />
                  <span>Asansol, West Bengal, India</span>
                </li>
                <li>
                  <FaCode />
                  <span>Available Worldwide (Remote)</span>
                </li>
              </ul>
              <div className="ft-badges">
                <span className="ft-badge">🚀 Remote Work</span>
                <span className="ft-badge ft-badge-info">💼 Open to Opportunities</span>
              </div>
            </div>
          </div>

          <hr className="ft-divider" />

          {/* Copyright */}
          <div className="ft-copyright">
            <p>
              © {currentYear} Subodh Paswan. Made with <FaHeart className="ft-heart" /> using React.js
            </p>
            <small>
              All rights reserved | Full Stack Developer | MERN Stack Specialist
            </small>
          </div>
        </div>

        {/* Scroll to Top Button */}
        <button 
          className="ft-scroll-top"
          id="scrollToTop"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      </footer>

      <script dangerouslySetInnerHTML={{
        __html: `
          window.addEventListener('scroll', function() {
            const scrollBtn = document.getElementById('scrollToTop');
            if (scrollBtn) {
              if (window.scrollY > 300) {
                scrollBtn.classList.add('visible');
              } else {
                scrollBtn.classList.remove('visible');
              }
            }
          });
        `
      }} />
    </>
  );
};

export default Footer;